import { useLazyQuery, useQuery } from '@apollo/client';
import {
  formatFullName,
  HouseDivisionType,
  IName,
  IResidentGroupCreate,
  IUpdateHouseDivision,
  IUpdateResident,
  ResidentGroupCreateSchema,
} from '@dokucoach/common-types';
import { Box } from '@material-ui/core';
import deleteIcon from 'assets/icon/delete.svg';
import { AdaptSelect } from 'components/AdaptField';
import AddButton from 'components/AddButton';
import Button from 'components/Button';
import CloseIconButton from 'components/CloseIconButton';
import DeleteButton from 'components/DeleteButton';
import Field from 'components/FastField';
import Popper, { MessageType } from 'components/Popper';
import { useContextSnackbar } from 'components/Snackbar/context';
import TextInput from 'components/TextInput';
import ThemeButton from 'components/ThemeButton';
import { Form, Formik, FormikProps } from 'formik';
import React, { Ref, useEffect, useMemo, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { GET_ALL_STATIONS_AND_LIVING_AREAS } from 'services/houseDivisions';
import { GET_ALL_RESIDENTS_BY_HOUSE_DIVISION, GET_RESIDENTS } from 'services/resident';
import { ResidentGroupPropType } from 'services/residentGroups';
import styled from 'styled-components';
import { byLastName } from 'utils/sortBy';

const TitleBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;

  h1 {
    margin: 0;
    font-size: 18px;
    line-height: 27px;
    color: ${(props) => props.theme.colors.nero};
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export type ResidentGroupUpdateBase = {
  id: string;
};

export type ResidentGroupValues = IResidentGroupCreate;
export type ResidentGroupUpdateValues = IResidentGroupCreate & ResidentGroupUpdateBase;

interface Props {
  show: boolean;
  isNew: boolean;
  group?: ResidentGroupPropType;
  onAddNew: (values: ResidentGroupValues) => void;
  onUpdate: (values: ResidentGroupUpdateValues) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

const ResidentGroupModal = ({
  show,
  isNew,
  group,
  onAddNew,
  onUpdate,
  onDelete,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { showApiError } = useContextSnackbar();
  const formRef = useRef<FormikProps<ResidentGroupValues>>();

  const initialValues: ResidentGroupValues = useMemo(
    () => ({
      name: isNew ? '' : '-',
      residents: [],
      isDefault: false,
      selectedHouseDivisionId: HouseDivisionType.INDIVIDUAL,
    }),
    [isNew],
  );

  const {
    data: stationAndLivingAreaData,
    error: stationAndLivingAreaError,
    loading: stationAndLivingAreaLoading,
  } = useQuery(GET_ALL_STATIONS_AND_LIVING_AREAS);

  const {
    data: residentsData,
    error: residentsError,
    loading: residentsLoading,
  } = useQuery(GET_RESIDENTS, {
    variables: { isDeleted: false },
  });

  const [
    getAllRelatedResidentsByHouseDivision,
    { data: residentAtDivisionData, error: residentAtDivisionError },
  ] = useLazyQuery(GET_ALL_RESIDENTS_BY_HOUSE_DIVISION);

  useEffect(() => {
    if (stationAndLivingAreaError) {
      showApiError(stationAndLivingAreaError);
    }
    if (residentsError) {
      showApiError(residentsError);
    }
  }, [stationAndLivingAreaError, residentsError]);

  useEffect(() => {
    if (residentAtDivisionError) {
      showApiError(residentAtDivisionError);
    }
    if (residentAtDivisionData) {
      formRef.current?.setFieldValue(
        'residents',
        residentAtDivisionData.getAllRelatedResidentsByHouseDivision
          .sort(byLastName)
          .map((resident: IUpdateResident) => resident.id),
      );
    }
  }, [residentAtDivisionData, residentAtDivisionError]);

  useEffect(() => {
    if (!group || !formRef.current) return;
    const residents = group.residents.map((r) => r.id);
    formRef.current.resetForm({
      values: {
        isDefault: group.isDefault,
        name: group.name,
        selectedHouseDivisionId: group.selectedHouseDivision?.id || HouseDivisionType.INDIVIDUAL,
        residents,
      },
    });
  }, [group, formRef]);

  const onSelectionChange = (value: string) => {
    formRef.current?.setFieldValue('selectedHouseDivisionId', value);

    if (value === HouseDivisionType.INDIVIDUAL) return;
    getAllRelatedResidentsByHouseDivision({ variables: { houseDivisionId: value } });
  };

  const onSave = () => {
    const values: ResidentGroupValues | undefined = formRef?.current?.values;
    if (!values) return;

    if (isNew) {
      onAddNew(values);
    } else {
      if (!group) return;
      onUpdate({
        id: group.id,
        name: values.name,
        residents: values.residents,
        isDefault: values.isDefault,
        selectedHouseDivisionId:
          values.selectedHouseDivisionId === HouseDivisionType.INDIVIDUAL
            ? null
            : values.selectedHouseDivisionId,
      });
    }
  };

  const onAddResident = (): void => {
    if (!formRef || !formRef.current) return;
    formRef.current.setFieldValue('residents', [
      ...(formRef.current.values.residents as string[]),
      undefined,
    ]);
    formRef.current?.setFieldValue('selectedHouseDivisionId', HouseDivisionType.INDIVIDUAL);
  };

  const onRemoveResident = (index: number): void => {
    const residents = [...((formRef.current?.values as ResidentGroupValues).residents as string[])];
    residents.splice(index, 1);
    formRef.current?.setFieldValue('residents', residents);
    formRef.current?.setFieldValue('selectedHouseDivisionId', HouseDivisionType.INDIVIDUAL);
  };

  return (
    <Modal show={show} backdrop="static" size="lg" centered>
      <Modal.Body data-test-id="resident-group-modal">
        <TitleBar>
          <h1>
            {isNew
              ? t('residentGroups_createNewGroup')
              : t('residentGroups_editGroup', { name: group?.name })}
          </h1>
          <CloseIconButton onClick={onClose} />
        </TitleBar>

        <Formik
          enableReinitialize={false}
          initialValues={{ ...initialValues }}
          onSubmit={onSave}
          innerRef={formRef as unknown as Ref<FormikProps<ResidentGroupValues>>}
          validationSchema={ResidentGroupCreateSchema}
          validateOnChange
          validateOnMount
        >
          {({ errors, values, isValid, setFieldValue }) => (
            <Form>
              <Box sx={{ mb: 3 }}>
                <Field
                  component={TextInput}
                  multiline
                  name="name"
                  rows={1}
                  editable={true}
                  label={t(`residentGroups_name`)}
                  suffix={
                    errors.name ? (
                      <Popper msg={t('validation_required')} type={MessageType.ERROR} />
                    ) : null
                  }
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                {!stationAndLivingAreaLoading ? (
                  <Field
                    component={AdaptSelect}
                    editable={true}
                    name="selectedHouseDivisionId"
                    label={t('residentGroups_selection')}
                    inputProps={{
                      onChange: (event: PointerEvent) =>
                        onSelectionChange((event.target as unknown as { value: string }).value),
                    }}
                    dropdownList={
                      stationAndLivingAreaData
                        ? [
                            {
                              label: t('residentGroup_individual_selection'),
                              value: HouseDivisionType.INDIVIDUAL,
                            },
                            ...stationAndLivingAreaData.getAllStationsAndLivingAreas.map(
                              (area: IUpdateHouseDivision) => ({
                                label: area.name,
                                value: area.id,
                              }),
                            ),
                          ]
                        : []
                    }
                  />
                ) : null}
              </Box>
              <p>{t('residentGroups_resident_subtitle')}</p>
              {!residentsLoading && ((values as ResidentGroupValues).residents as string[]).length
                ? ((values as ResidentGroupValues).residents as string[]).map(
                    (residentId, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Field
                          component={AdaptSelect}
                          name={`residents.[${index}]`}
                          label={t('residentGroups_name')}
                          editable={true}
                          disableError={true}
                          wrapper={{ width: '100%' }}
                          inputProps={{
                            onChange: () => {
                              setFieldValue(
                                'selectedHouseDivisionId',
                                HouseDivisionType.INDIVIDUAL,
                              );
                            },
                          }}
                          dropdownList={
                            residentsData
                              ? residentsData.getResidents
                                  .filter(
                                    // Filtering out the residents that have already been selected in any other year dropdown
                                    (resident: IUpdateResident) =>
                                      !(values.residents as string[])
                                        .filter(
                                          (id: string) =>
                                            id !== (values.residents as string[])[index],
                                        )
                                        .includes(resident.id),
                                  )
                                  .map((resident: IUpdateResident) => ({
                                    label: formatFullName(resident.name as IName),
                                    value: resident.id,
                                  }))
                              : []
                          }
                        />
                        <DeleteButton
                          index={`amputation-${index}`}
                          handleClickRemove={() => onRemoveResident(index)}
                        />
                      </Box>
                    ),
                  )
                : null}
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <AddButton
                  handleClickAdd={onAddResident}
                  label={t('residentGroups_addResident_button')}
                />
              </Box>
              <Actions>
                <ThemeButton
                  onClick={onSave}
                  data-test-id="resident-group-form-save-btn"
                  disabled={!isValid}
                >
                  {t('submit')}
                </ThemeButton>
                {!isNew && group && (
                  <Button
                    name="resident-group-delete-btn"
                    $isIcon={true}
                    $isAlert={true}
                    onClick={() => onDelete(group?.id)}
                    sx={{ ml: 1 }}
                  >
                    <SVG src={deleteIcon} />
                  </Button>
                )}
              </Actions>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default ResidentGroupModal;
